import React, {
    createContext, useContext, useState, useEffect, useCallback,
} from 'react';

const API = '/api/auth';

const AuthContext = createContext(null);

async function authFetch(path, options = {}) {
    const r = await fetch(`${API}${path}`, {
        credentials: 'include',              // важно: cookie с сессией
        headers: {
            'Content-Type': 'application/json',
            ...(options.headers || {}),
        },
        ...options,
    });
    return r;
}

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);         // { user_id, email } | null
    const [loading, setLoading] = useState(true);   // идёт первичная проверка /me
    const [configEnabled, setConfigEnabled] = useState(false);

    // ── Первичная загрузка: узнаём флаг + текущего пользователя ──
    useEffect(() => {
        let stop = false;
        (async () => {
            try {
                const r = await authFetch('/config');
                const cfg = await r.json();
                if (stop) return;
                setConfigEnabled(!!cfg.enabled);

                if (cfg.enabled) {
                    const rme = await authFetch('/me');
                    if (rme.ok) {
                        const data = await rme.json();
                        if (!stop && data?.user) setUser(data.user);
                    }
                    // 401 — не авторизован, это нормально
                }
            } catch (e) {
                console.warn('[Auth] init failed:', e);
            } finally {
                if (!stop) setLoading(false);
            }
        })();
        return () => { stop = true; };
    }, []);

    // ── Запрос кода ──
    const requestCode = useCallback(async (email) => {
        const r = await authFetch('/request-code', {
            method: 'POST',
            body: JSON.stringify({ email }),
        });
        if (!r.ok) {
            let msg = 'Не удалось отправить код';
            try { msg = (await r.json()).detail || msg; } catch (_) { }
            throw new Error(msg);
        }
        return r.json();
    }, []);

    // ── Проверка кода ──
    const verifyCode = useCallback(async (email, code) => {
        const r = await authFetch('/verify-code', {
            method: 'POST',
            body: JSON.stringify({ email, code }),
        });
        if (!r.ok) {
            let msg = 'Неверный код';
            try { msg = (await r.json()).detail || msg; } catch (_) { }
            throw new Error(msg);
        }
        const data = await r.json();
        setUser(data.user);
        return data.user;
    }, []);

    // ── Выход ──
    const logout = useCallback(async () => {
        try {
            await authFetch('/logout', { method: 'POST' });
        } catch (_) { }
        setUser(null);
    }, []);

    // ── Обновление /me (если что-то изменилось в другой вкладке) ──
    const refreshMe = useCallback(async () => {
        try {
            const r = await authFetch('/me');
            if (r.ok) {
                const data = await r.json();
                setUser(data.user);
            } else if (r.status === 401) {
                setUser(null);
            }
        } catch (_) { }
    }, []);

    const value = {
        user,
        loading,
        configEnabled,
        isAuthenticated: !!user,
        requestCode,
        verifyCode,
        logout,
        refreshMe,
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error('useAuth must be used within AuthProvider');
    return ctx;
}